/**
 * MemoryHub — dedicated "AI brain feeder" page.
 *
 * Lets the user pour structured context about their business into the
 * AI's memory: goals, company facts, preferences, vendors, tax notes,
 * whatever. Everything saved here is injected into the system prompt of
 * EVERY future chat in this workspace — both AI Studio and Brain mode.
 *
 * Categories are first-class:
 *   • Goals       — what the user/business is trying to achieve
 *   • Company     — structural facts (entity type, FY end, EIN, etc.)
 *   • Preferences — how the user likes things ("format Q1=Jan-Mar")
 *   • Clients     — recurring client-specific notes
 *   • Vendors     — vendors, contractors, recurring payees
 *   • Tax         — tax preferences, deductions, filing dates
 *   • Other       — free-form catch-all
 *
 * UX:
 *   - Each category is its own card with its own quick-add input.
 *   - Adding via the top-bar input picks a category from a dropdown.
 *   - Hover-to-reveal delete on each fact card.
 *   - Empty categories show a tiny "Add your first …" hint.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { apiFetch } from '../fetchInterceptor';
import { useWorkspace } from '../context/WorkspaceContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import {
  Brain, Target, Building2, Sliders, Receipt, FolderOpen,
  Plus, Trash2, Loader2, Sparkles, Pin, Wand2, RefreshCw,
  UserCog, Tag, Wallet, CalendarClock, Globe, MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Memory categories ─────────────────────────────────────────────────────
// `description` is the short helper line under the title (always shown).
// `starters` are clickable example chips that appear ONLY when a category is
// empty — they prefill the inline input on click so a user never stares at a
// blank box. Lowercase keys map to backend storage; titles are human-facing.
const CATEGORIES = [
  {
    key: 'Goals',
    icon: Target,
    color: 'text-emerald-500',
    description: 'What you\'re trying to achieve. Anchors every AI suggestion.',
    starters: [
      'Hit $1M ARR by end of fiscal year',
      'Cut SaaS spend by 20% before Q3',
      'Open a second location in Austin by 2027',
    ],
  },
  {
    key: 'Company',
    icon: Building2,
    color: 'text-blue-500',
    description: 'Entity type, EIN, fiscal year, address — system-level facts.',
    starters: [
      'We are an S-Corp incorporated in Delaware',
      'Fiscal year runs Jan 1 – Dec 31',
      'Mailing address: 123 Main St, Austin TX 78704',
    ],
  },
  {
    key: 'Team & Roles',
    icon: UserCog,
    color: 'text-cyan-500',
    description: 'Who does what, who to CC, who approves what.',
    starters: [
      'Sarah Chen is my COO — CC her on every P&L and cash-flow brief',
      'We have 12 employees: 4 sales, 5 eng, 3 ops',
      'Marcus approves all vendor expenses over $1K',
    ],
  },
  {
    key: 'Products & Pricing',
    icon: Tag,
    color: 'text-amber-500',
    description: 'What you sell and at what price — used by quote, invoice, and site copy.',
    starters: [
      'SaaS Pro plan: $99/mo · Enterprise: $499/mo',
      'Consulting hourly rate: $275/hr',
      'Minimum project size: $5K retainer',
    ],
  },
  {
    key: 'Cash Flow Habits',
    icon: Wallet,
    color: 'text-rose-500',
    description: 'Rhythms of money in and out — drives forecasts & anomaly alerts.',
    starters: [
      'Keep $50K minimum in operating account at all times',
      'Quarterly owner distributions on the 15th',
      'Net 30 invoicing standard; Net 15 for new clients',
    ],
  },
  {
    key: 'Routines & Cadence',
    icon: CalendarClock,
    color: 'text-orange-500',
    description: 'Recurring rhythm — the AI Brief will reference these.',
    starters: [
      'Reconcile banking every Friday morning',
      'Monthly close on the 5th of next month',
      'Renew all SaaS subscriptions in November',
    ],
  },
  {
    key: 'Industry & Market',
    icon: Globe,
    color: 'text-teal-500',
    description: 'What makes your business different — without this, AI keeps asking.',
    starters: [
      'Residential plumbing serving Austin metro',
      'We compete with Roto-Rooter and local independents',
      'Busy season May–September; avg ticket $450',
    ],
  },
  {
    key: 'Brand Voice',
    icon: MessageSquare,
    color: 'text-pink-500',
    description: 'How you sound to customers — used by AI emails, briefs, and site copy.',
    starters: [
      'Plain English. No jargon. No exclamation marks',
      'Sign every email "Stay focused. — Eli"',
      'Confident but never salesy — Patagonia, not Used Car Lot',
    ],
  },
  {
    key: 'Tax',
    icon: Receipt,
    color: 'text-fuchsia-500',
    description: 'Tax preferences, deductions you always claim, deadlines.',
    starters: [
      'Elected S-Corp tax treatment in 2023',
      'Home office deduction: 12% of expenses',
      'Estimated taxes due Jan 15 / Apr 15 / Jun 15 / Sep 15',
    ],
  },
  {
    key: 'Preferences',
    icon: Sliders,
    color: 'text-violet-500',
    description: 'How you like reports, naming, formats, integrations.',
    starters: [
      'Show all currency with thousands separators',
      'Email reports as PDF, not CSV',
      'Use accrual basis for all reports unless specified',
    ],
  },
  {
    key: 'Other',
    icon: FolderOpen,
    color: 'text-zinc-500',
    description: 'Anything that doesn\'t fit a category — free-form catch-all.',
    starters: [
      'Prefer focus blocks before 11am — no meetings',
      'I\'m the sole founder, married with 2 kids',
    ],
  },
];

export default function MemoryHub() {
  const { currentWorkspace } = useWorkspace();
  const [facts, setFacts] = useState([]);
  const [loading, setLoading] = useState(false);
  // Top-bar quick-add (picks a category)
  const [quickInput, setQuickInput] = useState('');
  const [quickCategory, setQuickCategory] = useState('Goals');
  const [adding, setAdding] = useState(false);
  // Sync-from-chat: scans recent conversations via a cheap model and
  // surfaces extracted memories for one-click approval. State machine:
  //   idle → scanning → reviewing (modal open) → saving → idle.
  const [syncing, setSyncing] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [syncMeta, setSyncMeta] = useState({ scanned_messages: 0, scanned_threads: 0, days: 30 });
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [savingProposals, setSavingProposals] = useState(false);
  // Pending digest — pre-computed by the Sunday brain digest job. When set,
  // the "Sync from chat" button shows a small "N new" badge and clicking it
  // opens the modal pre-loaded with the digest's proposals instead of
  // triggering a fresh Gemini call.
  const [pendingDigest, setPendingDigest] = useState(null);

  const wsId = currentWorkspace?.workspace_id;

  const load = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const r = await apiFetch(`${API_URL}/api/memory/facts?workspace_id=${wsId}`);
      if (r.ok) {
        const data = await r.json();
        setFacts(data.facts || []);
      }
    } catch {
      toast.error('Could not load memories');
    } finally {
      setLoading(false);
    }
  }, [wsId]);

  useEffect(() => { load(); }, [load]);

  // Poll the Sunday-digest endpoint when the page mounts. If there's a
  // pending pre-computed digest, surface it via the Sync button badge.
  useEffect(() => {
    if (!wsId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await apiFetch(`${API_URL}/api/memory/digest/latest?workspace_id=${wsId}`);
        if (r.ok && !cancelled) {
          const data = await r.json();
          if (data.pending) setPendingDigest(data);
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [wsId]);

  const addFact = async (content, category) => {
    const text = (content || '').trim();
    if (!text || !wsId) return null;
    setAdding(true);
    try {
      const r = await apiFetch(`${API_URL}/api/memory/facts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspace_id: wsId,
          content: text,
          category: category || null,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save');
        return null;
      }
      const fact = await r.json();
      setFacts((prev) => [fact, ...prev]);
      toast.success(`Saved to ${category || 'Other'}`);
      return fact;
    } catch {
      toast.error('Network error');
      return null;
    } finally {
      setAdding(false);
    }
  };

  const deleteFact = async (id) => {
    try {
      const r = await apiFetch(`${API_URL}/api/memory/facts/${id}`, { method: 'DELETE' });
      if (r.ok) {
        setFacts((prev) => prev.filter((f) => f.memory_id !== id));
        toast.success('Removed');
      }
    } catch {
      toast.error('Could not delete');
    }
  };

  // Sync from chat: ask the cheap LLM to propose durable facts from the
  // last 30 days of conversations. Opens an approval modal with all
  // proposals pre-selected (user can uncheck / edit / re-category each).
  const runSync = async () => {
    if (!wsId) return;
    // If a Sunday digest is pending, skip the live Gemini call — surface
    // the pre-computed proposals instantly and mark the digest consumed.
    if (pendingDigest && (pendingDigest.proposals || []).length > 0) {
      setSyncMeta({
        scanned_messages: 0,
        scanned_threads: 0,
        days: 14,
        from_digest: true,
        week_key: pendingDigest.week_key,
      });
      setProposals(pendingDigest.proposals.map((p) => ({ ...p, selected: true })));
      setShowSyncModal(true);
      try {
        await apiFetch(`${API_URL}/api/memory/digest/${pendingDigest.digest_id}/consume`, { method: 'POST' });
      } catch { /* silent */ }
      setPendingDigest(null);
      return;
    }
    setSyncing(true);
    try {
      const r = await apiFetch(`${API_URL}/api/memory/sync-from-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspace_id: wsId, days: 30, max_proposals: 12 }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Sync failed');
        return;
      }
      const data = await r.json();
      setSyncMeta({
        scanned_messages: data.scanned_messages || 0,
        scanned_threads: data.scanned_threads || 0,
        days: data.days || 30,
      });
      setProposals((data.proposals || []).map((p) => ({ ...p, selected: true })));
      setShowSyncModal(true);
      if ((data.proposals || []).length === 0) {
        toast.message('Nothing new to remember', {
          description: `Scanned ${data.scanned_messages || 0} messages — no durable facts found.`,
        });
      }
    } catch {
      toast.error('Network error during sync');
    } finally {
      setSyncing(false);
    }
  };

  const saveApproved = async () => {
    const chosen = proposals.filter((p) => p.selected);
    if (chosen.length === 0) {
      setShowSyncModal(false);
      return;
    }
    setSavingProposals(true);
    try {
      const r = await apiFetch(`${API_URL}/api/memory/approve-proposals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspace_id: wsId,
          facts: chosen.map((p) => ({
            content: p.content,
            category: p.category,
          })),
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Save failed');
        return;
      }
      const data = await r.json();
      setFacts((prev) => [...(data.saved || []), ...prev]);
      toast.success(`Saved ${data.count} ${data.count === 1 ? 'memory' : 'memories'}`);
      setShowSyncModal(false);
      setProposals([]);
    } catch {
      toast.error('Network error');
    } finally {
      setSavingProposals(false);
    }
  };

  // Bucket facts by category for display. Legacy facts whose category no
  // longer exists in CATEGORIES (e.g. "Clients", "Vendors" from before this
  // redesign) fall back to "Other" so they remain visible until the user
  // moves or deletes them.
  const byCategory = useMemo(() => {
    const map = {};
    const known = new Set(CATEGORIES.map((c) => c.key));
    for (const c of CATEGORIES) map[c.key] = [];
    for (const f of facts) {
      let cat = (f.category || '').trim() || 'Other';
      if (!known.has(cat)) cat = 'Other';
      map[cat].push(f);
    }
    return map;
  }, [facts]);

  const totalCount = facts.length;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="memory-hub-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end gap-4 sm:gap-6 justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-emerald-500 flex items-center justify-center shadow-lg shadow-violet-500/20">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">AI Memory</h1>
            {totalCount > 0 && (
              <Badge variant="outline" className="ml-1 text-xs" data-testid="memory-total-count">
                {totalCount} {totalCount === 1 ? 'fact' : 'facts'}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Feed the AI everything it should remember about <strong>{currentWorkspace?.name || 'this workspace'}</strong>.
            Goals, products, cash-flow habits, brand voice — anything. Every chat in this workspace will be grounded in what you save here.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={runSync}
            disabled={syncing || !wsId}
            data-testid="memory-sync-btn"
            className="relative border-violet-200 dark:border-violet-800 hover:bg-violet-50 dark:hover:bg-violet-950/40 text-violet-700 dark:text-violet-300"
            title="Scan the last 30 days of conversations and propose new memories"
          >
            {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Wand2 className="w-3.5 h-3.5 mr-1.5" />}
            Sync from chat
            {pendingDigest && pendingDigest.count > 0 && (
              <span
                className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-emerald-500 text-white shadow"
                data-testid="memory-sync-badge"
              >
                {pendingDigest.count}
              </span>
            )}
          </Button>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Sparkles className="w-3.5 h-3.5 text-violet-500" />
            <span className="hidden lg:inline">Tip: type <code className="bg-muted px-1.5 py-0.5 rounded">/remember &lt;fact&gt;</code> in any chat.</span>
          </div>
        </div>
      </div>

      {/* Quick-add bar — picks a category, then a longer textarea so the user
          can paste a paragraph of context if they want. */}
      <Card className="border-violet-200 dark:border-violet-900/40 bg-violet-50/30 dark:bg-violet-950/10">
        <CardContent className="pt-5 space-y-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-violet-500" />
            <h2 className="text-sm font-semibold">Add a memory</h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Select value={quickCategory} onValueChange={setQuickCategory}>
              <SelectTrigger className="sm:w-44 shrink-0" data-testid="memory-quick-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => {
                  const Icon = c.icon;
                  return (
                    <SelectItem key={c.key} value={c.key}>
                      <span className="inline-flex items-center gap-2">
                        <Icon className={`w-3.5 h-3.5 ${c.color}`} />
                        {c.key}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <Textarea
              value={quickInput}
              onChange={(e) => setQuickInput(e.target.value)}
              placeholder="e.g. We file taxes as an S-Corp. Our fiscal year ends June 30. Primary contact at the bank is Jamie."
              className="flex-1 min-h-[60px] resize-y"
              data-testid="memory-quick-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  addFact(quickInput, quickCategory).then((fact) => {
                    if (fact) setQuickInput('');
                  });
                }
              }}
            />
            <Button
              onClick={() => {
                addFact(quickInput, quickCategory).then((fact) => {
                  if (fact) setQuickInput('');
                });
              }}
              disabled={!quickInput.trim() || adding}
              className="sm:w-32 shrink-0"
              data-testid="memory-quick-add-btn"
            >
              {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Plus className="w-3.5 h-3.5 mr-1" />Save</>}
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            ⌘/Ctrl + Enter to save quickly. Up to 500 characters per memory.
          </p>
        </CardContent>
      </Card>

      {/* Categorized grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {CATEGORIES.map((cat) => (
          <CategoryCard
            key={cat.key}
            category={cat}
            facts={byCategory[cat.key] || []}
            onAdd={(text) => addFact(text, cat.key)}
            onDelete={deleteFact}
            adding={adding}
          />
        ))}
      </div>

      {loading && (
        <div className="text-center text-xs text-muted-foreground py-6">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading memories…
        </div>
      )}

      {/* Sync-from-chat approval modal — surfaced after a successful scan
          completes. Every proposal pre-selected; uncheck to skip, change
          category via dropdown, then "Save selected" to commit. Nothing
          persists until the user clicks save. */}
      <Dialog open={showSyncModal} onOpenChange={setShowSyncModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="memory-sync-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-violet-500" />
              Proposed memories from your chats
            </DialogTitle>
            <DialogDescription>
              {syncMeta.from_digest
                ? `Pre-computed by Sunday Brain Digest for week ${syncMeta.week_key}. Uncheck anything you don't want to keep, change category if needed, then save.`
                : `Scanned ${syncMeta.scanned_messages.toLocaleString()} messages across ${syncMeta.scanned_threads} ${syncMeta.scanned_threads === 1 ? 'thread' : 'threads'} in the last ${syncMeta.days} days. Uncheck anything you don't want to keep, change category if needed, then save.`}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-2 -mx-2 px-2 py-2" data-testid="memory-sync-proposals-list">
            {proposals.length === 0 && (
              <p className="text-sm text-muted-foreground py-8 text-center">
                No durable facts found in your recent chats. Pin a few manually above, then come back later.
              </p>
            )}
            {proposals.map((p, idx) => (
              <div
                key={idx}
                className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors ${
                  p.selected
                    ? 'border-violet-300 dark:border-violet-700 bg-violet-50/40 dark:bg-violet-950/20'
                    : 'border-border bg-muted/30 opacity-60'
                }`}
                data-testid={`memory-sync-proposal-${idx}`}
              >
                <Checkbox
                  checked={p.selected}
                  onCheckedChange={(v) => {
                    setProposals((prev) => prev.map((q, i) => i === idx ? { ...q, selected: !!v } : q));
                  }}
                  className="mt-1"
                  data-testid={`memory-sync-check-${idx}`}
                />
                <div className="flex-1 min-w-0 space-y-1.5">
                  <Textarea
                    value={p.content}
                    onChange={(e) => {
                      setProposals((prev) => prev.map((q, i) => i === idx ? { ...q, content: e.target.value } : q));
                    }}
                    rows={2}
                    className="text-xs min-h-[44px] resize-none"
                    disabled={!p.selected}
                    data-testid={`memory-sync-content-${idx}`}
                  />
                  <Select
                    value={p.category || 'Other'}
                    onValueChange={(v) => {
                      setProposals((prev) => prev.map((q, i) => i === idx ? { ...q, category: v } : q));
                    }}
                    disabled={!p.selected}
                  >
                    <SelectTrigger className="h-7 w-44 text-xs" data-testid={`memory-sync-category-${idx}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c.key} value={c.key} className="text-xs">{c.key}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>

          <DialogFooter className="flex items-center justify-between gap-2 sm:justify-between mt-2 pt-3 border-t">
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <button
                type="button"
                onClick={() => setProposals((prev) => prev.map((p) => ({ ...p, selected: true })))}
                className="hover:text-foreground transition-colors"
                data-testid="memory-sync-select-all"
                disabled={proposals.length === 0}
              >
                Select all
              </button>
              <span>·</span>
              <button
                type="button"
                onClick={() => setProposals((prev) => prev.map((p) => ({ ...p, selected: false })))}
                className="hover:text-foreground transition-colors"
                data-testid="memory-sync-deselect-all"
                disabled={proposals.length === 0}
              >
                Deselect all
              </button>
              <span>·</span>
              <button
                type="button"
                onClick={runSync}
                className="hover:text-foreground transition-colors inline-flex items-center gap-1"
                data-testid="memory-sync-rescan"
                disabled={syncing}
              >
                <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} /> Re-scan
              </button>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowSyncModal(false)} data-testid="memory-sync-cancel">
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={saveApproved}
                disabled={savingProposals || proposals.filter((p) => p.selected).length === 0}
                className="bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="memory-sync-save"
              >
                {savingProposals
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                  : <Plus className="w-3.5 h-3.5 mr-1.5" />}
                Save {proposals.filter((p) => p.selected).length} selected
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CategoryCard({ category, facts, onAdd, onDelete, adding }) {
  const Icon = category.icon;
  const [input, setInput] = useState('');
  const [showInput, setShowInput] = useState(false);

  const handleAdd = async () => {
    const text = input.trim();
    if (!text) return;
    const fact = await onAdd(text);
    if (fact) {
      setInput('');
      setShowInput(false);
    }
  };

  return (
    <Card data-testid={`memory-category-${category.key.toLowerCase()}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Icon className={`w-4 h-4 ${category.color}`} />
            {category.key}
            <Badge variant="secondary" className="text-[10px] py-0 px-1.5 h-5">{facts.length}</Badge>
          </CardTitle>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowInput((v) => !v)}
            className="h-7 w-7 p-0"
            data-testid={`memory-add-toggle-${category.key.toLowerCase()}`}
            title={`Add to ${category.key}`}
          >
            <Plus className="w-3.5 h-3.5" />
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground -mt-0.5">{category.description}</p>
      </CardHeader>
      <CardContent className="space-y-2">
        {showInput && (
          <div className="flex items-start gap-2">
            <Input
              autoFocus
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') { setInput(''); setShowInput(false); } }}
              placeholder={`Add to ${category.key}…`}
              className="h-8 text-xs"
              data-testid={`memory-inline-input-${category.key.toLowerCase()}`}
            />
            <Button
              size="sm"
              onClick={handleAdd}
              disabled={!input.trim() || adding}
              className="h-8"
              data-testid={`memory-inline-add-${category.key.toLowerCase()}`}
            >
              {adding ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Add'}
            </Button>
          </div>
        )}
        {facts.length === 0 && !showInput && (
          <div className="py-2" data-testid={`memory-empty-${category.key.toLowerCase()}`}>
            <p className="text-[11px] text-muted-foreground mb-2 italic">
              💡 Try adding one of these — click to use:
            </p>
            <div className="flex flex-wrap gap-1.5">
              {(category.starters || []).map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => { setInput(s); setShowInput(true); }}
                  className="text-[11px] px-2.5 py-1 rounded-full border border-dashed border-border bg-muted/40 hover:bg-accent hover:border-solid text-muted-foreground hover:text-foreground transition-colors text-left max-w-full"
                  data-testid={`memory-starter-${category.key.toLowerCase()}-${i}`}
                  title="Click to prefill — you can edit before saving"
                >
                  "{s.length > 80 ? s.slice(0, 77) + '…' : s}"
                </button>
              ))}
            </div>
          </div>
        )}
        {facts.map((f) => (
          <div
            key={f.memory_id}
            className="group flex items-start gap-2 rounded-md border border-border bg-card px-3 py-2 hover:bg-accent/40 transition-colors"
            data-testid={`memory-fact-${f.memory_id}`}
          >
            <Pin className={`w-3 h-3 mt-1 flex-shrink-0 ${f.kind === 'explicit' ? category.color : 'text-zinc-400'}`} />
            <p className="text-xs text-foreground break-words flex-1 leading-snug">{f.content}</p>
            <button
              type="button"
              onClick={() => onDelete(f.memory_id)}
              className="opacity-0 group-hover:opacity-100 transition-opacity text-zinc-400 hover:text-red-500 shrink-0 mt-0.5"
              data-testid={`memory-delete-${f.memory_id}`}
              title="Remove"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
